import { create } from 'zustand';
import * as api from '../api/endpoints';

interface CollectedEntry {
  collectedAt: string;
}

interface CollectionState {
  diagrams: Record<string, CollectedEntry>;  // key = filename
  plans: Record<string, CollectedEntry>;      // key = "file:page"
  diagramModifiedAt: Record<string, string>;  // filename → ISO timestamp
  planModifiedAt: Record<string, string>;     // "file:page" → ISO timestamp
  planDataVersion: number;  // bumped after auto-match completes

  collectDiagram: (file: string) => void;
  uncollectDiagram: (file: string) => void;
  collectPlan: (file: string, page: number) => void;
  uncollectPlan: (file: string, page: number) => void;
  touchDiagram: (file: string) => void;
  touchPlan: (file: string, page: number) => void;
  isDiagramStale: (file: string) => boolean;
  isPlanStale: (file: string, page: number) => boolean;
  clearAll: () => void;
  restore: (d: Record<string, CollectedEntry>, p: Record<string, CollectedEntry>) => void;
}

export const useCollectionStore = create<CollectionState>((set, get) => ({
  diagrams: {},
  plans: {},
  diagramModifiedAt: {},
  planModifiedAt: {},
  planDataVersion: 0,

  collectDiagram: (file) => {
    const entry = { collectedAt: new Date().toISOString() };
    set((s) => ({ diagrams: { ...s.diagrams, [file]: entry } }));
    api.collectFile(file, 'diagrams');
  },

  uncollectDiagram: (file) => {
    set((s) => {
      const next = { ...s.diagrams };
      delete next[file];
      return { diagrams: next };
    });
    api.uncollectFile(file, 'diagrams');
  },

  collectPlan: (file, page) => {
    const key = `${file}:${page}`;
    const entry = { collectedAt: new Date().toISOString() };
    set((s) => {
      const planModifiedAt = { ...s.planModifiedAt };
      delete planModifiedAt[key]; // clear stale flag
      return { plans: { ...s.plans, [key]: entry }, planModifiedAt };
    });
    api.collectFile(file, 'plans', page);
    // Auto-match so review data appears immediately
    api.matchPlan(file, page)
      .then(() => set((s) => ({ planDataVersion: s.planDataVersion + 1 })))
      .catch(() => {});
  },

  uncollectPlan: (file, page) => {
    const key = `${file}:${page}`;
    set((s) => {
      const next = { ...s.plans };
      delete next[key];
      return { plans: next };
    });
    api.uncollectFile(file, 'plans', page);
  },

  touchDiagram: (file) =>
    set((s) => ({ diagramModifiedAt: { ...s.diagramModifiedAt, [file]: new Date().toISOString() } })),

  touchPlan: (file, page) => {
    const key = `${file}:${page}`;
    set((s) => ({ planModifiedAt: { ...s.planModifiedAt, [key]: new Date().toISOString() } }));
  },

  isDiagramStale: (file) => {
    const s = get();
    const collected = s.diagrams[file];
    const modified = s.diagramModifiedAt[file];
    return !!(collected && modified && modified > collected.collectedAt);
  },

  isPlanStale: (file, page) => {
    const key = `${file}:${page}`;
    const s = get();
    const collected = s.plans[key];
    const modified = s.planModifiedAt[key];
    return !!(collected && modified && modified > collected.collectedAt);
  },

  clearAll: () => set({ diagrams: {}, plans: {}, diagramModifiedAt: {}, planModifiedAt: {}, planDataVersion: 0 }),

  restore: (d, p) => set({ diagrams: d, plans: p }),
}));
